import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';

// ── Icons ────────────────────────────────────────────────────────────────────
function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
function VoicemailIcon({ className }) {
  return <Ico className={className}><circle cx="6" cy="12" r="3"/><circle cx="18" cy="12" r="3"/><path d="M9 12h6"/></Ico>;
}
function RefreshIcon({ className }) {
  return <Ico className={className}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.67"/></Ico>;
}
function PlayIcon({ className }) {
  return <Ico className={className}><polygon points="5 3 19 12 5 21 5 3"/></Ico>;
}
function PauseIcon({ className }) {
  return <Ico className={className}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></Ico>;
}
function DownloadIcon({ className }) {
  return <Ico className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></Ico>;
}
function TrashIcon({ className }) {
  return <Ico className={className}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></Ico>;
}
function CheckIcon({ className }) {
  return <Ico className={className}><polyline points="20 6 9 17 4 12"/></Ico>;
}
function MicIcon({ className }) {
  return <Ico className={className}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></Ico>;
}
function UploadIcon({ className }) {
  return <Ico className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></Ico>;
}
function TypeIcon({ className }) {
  return <Ico className={className}><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></Ico>;
}
function ChevronDownIcon({ className }) {
  return <Ico className={className}><polyline points="6 9 12 15 18 9"/></Ico>;
}
function StopIcon({ className }) {
  return <Ico className={className}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></Ico>;
}

// ── WAV encoder (converts AudioBuffer → WAV Blob) ────────────────────────────
function audioBufferToWav(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate  = audioBuffer.sampleRate;
  const length      = audioBuffer.length;
  const bitDepth    = 16;
  const bytesPerSample = bitDepth / 8;
  const dataLen     = length * numChannels * bytesPerSample;
  const buf         = new ArrayBuffer(44 + dataLen);
  const view        = new DataView(buf);

  const str = (offset, s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i)); };
  const u32 = (offset, v) => view.setUint32(offset, v, true);
  const u16 = (offset, v) => view.setUint16(offset, v, true);

  str(0,  'RIFF');
  u32(4,  36 + dataLen);
  str(8,  'WAVE');
  str(12, 'fmt ');
  u32(16, 16);
  u16(20, 1);                         // PCM
  u16(22, numChannels);
  u32(24, sampleRate);
  u32(28, sampleRate * numChannels * bytesPerSample);
  u16(32, numChannels * bytesPerSample);
  u16(34, bitDepth);
  str(36, 'data');
  u32(40, dataLen);

  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const s = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([buf], { type: 'audio/wav' });
}

// Convert a recorded Blob (webm/ogg) → WAV Blob via AudioContext
async function blobToWav(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    return audioBufferToWav(audioBuffer);
  } finally {
    audioCtx.close();
  }
}

// ── Greeting Panel ────────────────────────────────────────────────────────────
function GreetingPanel({ theme }) {
  const isDark = theme === 'dark';

  // Shared state
  const [expanded, setExpanded]       = useState(false);
  const [activeTab, setActiveTab]     = useState('text'); // 'text' | 'record' | 'upload'
  const [hasAudio, setHasAudio]       = useState(false);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(''); // confirmed server URL
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [msg, setMsg]                 = useState({ type: '', text: '' });

  // Text tab
  const [greeting, setGreeting]       = useState('');
  const [greetingDraft, setGreetingDraft] = useState('');
  const [greetingLoading, setGreetingLoading] = useState(false);

  // Record tab
  const [recording, setRecording]     = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  const [recordSecs, setRecordSecs]   = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const timerRef         = useRef(null);

  // Upload tab
  const [uploadFile, setUploadFile]   = useState(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const fileInputRef = useRef(null);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  // Load text greeting + audio status on mount
  useEffect(() => {
    (async () => {
      setGreetingLoading(true);
      try {
        const [greetRes, audioRes] = await Promise.all([
          api.get('/voicemails/greeting'),
          api.get('/voicemails/greeting-audio/info'),
        ]);
        setGreeting(greetRes.data.greeting || '');
        setGreetingDraft(greetRes.data.greeting || '');
        setHasAudio(audioRes.data.hasAudio);
        if (audioRes.data.hasAudio) setAudioPreviewUrl(audioRes.data.audioUrl);
      } catch (_) {}
      setGreetingLoading(false);
    })();
  }, []);

  // Cleanup object URLs
  useEffect(() => () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    if (uploadPreview) URL.revokeObjectURL(uploadPreview);
  }, []);

  // ── Text save ─────────────────────────────────────────────────────────────
  const saveText = async () => {
    if (!greetingDraft.trim()) { flash('error', 'Greeting cannot be empty.'); return; }
    setSaving(true);
    try {
      const res = await api.put('/voicemails/greeting', { greeting: greetingDraft });
      setGreeting(res.data.greeting);
      setGreetingDraft(res.data.greeting);
      flash('success', '✓ Text greeting saved');
    } catch (e) {
      flash('error', e?.response?.data?.message || 'Failed to save.');
    }
    setSaving(false);
  };

  // ── Record helpers ────────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const raw = new Blob(chunksRef.current, { type: mr.mimeType });
        try {
          const wav = await blobToWav(raw);
          if (recordedUrl) URL.revokeObjectURL(recordedUrl);
          const url = URL.createObjectURL(wav);
          setRecordedBlob(wav);
          setRecordedUrl(url);
        } catch {
          // If decode fails (very short clip), discard
          flash('error', 'Recording too short or unsupported. Try again.');
        }
      };
      mr.start(100);
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordSecs(0);
      timerRef.current = setInterval(() => setRecordSecs(s => s + 1), 1000);
    } catch {
      flash('error', 'Microphone access denied.');
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
  };

  const discardRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl('');
    setRecordSecs(0);
  };

  const saveRecording = async () => {
    if (!recordedBlob) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', recordedBlob, 'greeting.wav');
      await api.post('/voicemails/greeting-audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const info = await api.get('/voicemails/greeting-audio/info');
      setHasAudio(true);
      setAudioPreviewUrl(info.data.audioUrl);
      discardRecording();
      flash('success', '✓ Audio greeting saved');
    } catch (e) {
      flash('error', e?.response?.data?.message || 'Upload failed.');
    }
    setSaving(false);
  };

  // ── Upload helpers ────────────────────────────────────────────────────────
  const handleFileSelect = (file) => {
    if (!file) return;
    const ok = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/wave', 'audio/x-wav'].includes(file.type.toLowerCase())
      || file.name.match(/\.(mp3|wav)$/i);
    if (!ok) { flash('error', 'Only MP3 and WAV files are accepted.'); return; }
    if (file.size > 10 * 1024 * 1024) { flash('error', 'File must be under 10 MB.'); return; }
    if (uploadPreview) URL.revokeObjectURL(uploadPreview);
    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
  };

  const saveUpload = async () => {
    if (!uploadFile) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile, uploadFile.name);
      await api.post('/voicemails/greeting-audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const info = await api.get('/voicemails/greeting-audio/info');
      setHasAudio(true);
      setAudioPreviewUrl(info.data.audioUrl);
      setUploadFile(null);
      if (uploadPreview) { URL.revokeObjectURL(uploadPreview); setUploadPreview(''); }
      flash('success', '✓ Audio greeting saved');
    } catch (e) {
      flash('error', e?.response?.data?.message || 'Upload failed.');
    }
    setSaving(false);
  };

  // ── Delete audio greeting ─────────────────────────────────────────────────
  const deleteAudio = async () => {
    setDeleting(true);
    try {
      await api.delete('/voicemails/greeting-audio');
      setHasAudio(false);
      setAudioPreviewUrl('');
      flash('success', 'Audio greeting removed. TTS text greeting is now active.');
    } catch (e) {
      flash('error', 'Failed to remove audio greeting.');
    }
    setDeleting(false);
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const text      = isDark ? 'text-white'      : 'text-slate-900';
  const textMuted = isDark ? 'text-white/50'   : 'text-slate-500';
  const border    = isDark ? 'border-white/10' : 'border-slate-200';
  const surface   = isDark ? 'bg-white/[0.03]' : 'bg-slate-50';
  const tabActive = isDark
    ? 'bg-white/10 text-white border-white/20'
    : 'bg-white text-slate-900 border-slate-300 shadow-sm';
  const tabInactive = isDark
    ? 'text-white/40 hover:text-white/70 border-transparent'
    : 'text-slate-500 hover:text-slate-700 border-transparent';

  const fmtSecs = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className={`mb-5 border rounded-2xl overflow-hidden transition-colors ${border} ${surface}`}>
      {/* Accordion header */}
      <button
        onClick={() => setExpanded(e => !e)}
        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition ${
          isDark ? 'hover:bg-white/5 text-white/80' : 'hover:bg-slate-100 text-slate-700'
        }`}
      >
        <div className="flex items-center gap-2">
          <MicIcon className={`h-4 w-4 ${isDark ? 'text-white/50' : 'text-slate-400'}`} />
          <span>Voicemail Greeting</span>
          {!greetingLoading && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              hasAudio
                ? (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700')
                : (isDark ? 'bg-white/10 text-white/40' : 'bg-slate-200 text-slate-500')
            }`}>
              {hasAudio ? 'Audio' : 'Text (TTS)'}
            </span>
          )}
        </div>
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${
          isDark ? 'text-white/30' : 'text-slate-400'
        } ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Accordion body */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="greeting-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`px-4 pb-4 pt-2 border-t ${border}`}>

              {/* Active audio greeting preview */}
              {hasAudio && audioPreviewUrl && (
                <div className={`mb-4 p-3 rounded-xl border ${
                  isDark ? 'bg-green-500/10 border-green-500/25' : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                      ✓ Active: Custom Audio Greeting
                    </span>
                    <button
                      onClick={deleteAudio}
                      disabled={deleting}
                      className={`text-xs px-2 py-0.5 rounded-lg border transition disabled:opacity-50 ${
                        isDark
                          ? 'bg-red-500/15 hover:bg-red-500/25 text-red-400 border-red-500/25'
                          : 'bg-red-50 hover:bg-red-100 text-red-600 border-red-200'
                      }`}
                    >
                      {deleting ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                  <audio controls className="w-full h-8">
                    <source src={audioPreviewUrl} />
                  </audio>
                </div>
              )}

              {/* Tab switcher */}
              <div className={`flex gap-1 mb-4 p-1 rounded-xl border ${
                isDark ? 'bg-white/5 border-white/8' : 'bg-slate-100 border-slate-200'
              }`}>
                {[
                  { id: 'text',   label: 'Text',   Icon: TypeIcon },
                  { id: 'record', label: 'Record',  Icon: MicIcon },
                  { id: 'upload', label: 'Upload',  Icon: UploadIcon },
                ].map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-medium border transition ${
                      activeTab === id ? tabActive : tabInactive
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              {/* ── Text tab ── */}
              {activeTab === 'text' && (
                <div>
                  <p className={`text-xs mb-3 ${textMuted}`}>
                    This message is spoken to callers via text-to-speech. Used when no audio greeting is set.
                  </p>
                  <div className="relative">
                    <textarea
                      value={greetingDraft}
                      onChange={e => { if (e.target.value.length <= 500) setGreetingDraft(e.target.value); }}
                      rows={3}
                      placeholder="Enter your voicemail greeting…"
                      className={`w-full rounded-xl border px-3 py-2.5 text-sm resize-none outline-none transition focus:ring-2 ${
                        isDark
                          ? 'bg-white/8 border-white/12 text-white placeholder-white/25 focus:ring-sky-500/40 focus:border-sky-500/50'
                          : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:ring-blue-300 focus:border-blue-400'
                      }`}
                    />
                    <span className={`absolute bottom-2 right-3 text-xs pointer-events-none ${
                      greetingDraft.length > 480
                        ? (isDark ? 'text-red-400' : 'text-red-500')
                        : (isDark ? 'text-white/25' : 'text-slate-400')
                    }`}>{greetingDraft.length}/500</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={saveText}
                      disabled={saving || greetingDraft === greeting}
                      className={`px-4 py-1.5 rounded-xl text-sm font-medium border transition disabled:opacity-40 ${
                        isDark
                          ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border-sky-500/30'
                          : 'bg-slate-900 hover:bg-slate-700 text-white border-transparent'
                      }`}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    {greetingDraft !== greeting && (
                      <button
                        onClick={() => { setGreetingDraft(greeting); }}
                        className={`px-3 py-1.5 rounded-xl text-sm transition ${
                          isDark ? 'text-white/40 hover:text-white/60' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Discard
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Record tab ── */}
              {activeTab === 'record' && (
                <div>
                  <p className={`text-xs mb-4 ${textMuted}`}>
                    Record your greeting live using your microphone. The recording will be saved as a WAV file and used instead of the text-to-speech greeting.
                  </p>

                  {!recordedBlob ? (
                    <div className="flex flex-col items-center gap-4">
                      {/* Record / Stop button */}
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={recording ? stopRecording : startRecording}
                        className={`w-20 h-20 rounded-full flex flex-col items-center justify-center gap-1 border-2 transition ${
                          recording
                            ? (isDark
                                ? 'bg-red-500/20 border-red-500/60 text-red-400'
                                : 'bg-red-50 border-red-400 text-red-600')
                            : (isDark
                                ? 'bg-white/8 border-white/20 text-white/70 hover:bg-white/12'
                                : 'bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200')
                        }`}
                      >
                        {recording ? (
                          <>
                            <motion.div
                              animate={{ scale: [1, 1.15, 1] }}
                              transition={{ repeat: Infinity, duration: 1 }}
                            >
                              <StopIcon className="h-7 w-7" />
                            </motion.div>
                            <span className="text-xs font-mono font-semibold">{fmtSecs(recordSecs)}</span>
                          </>
                        ) : (
                          <>
                            <MicIcon className="h-7 w-7" />
                            <span className="text-xs font-medium">Record</span>
                          </>
                        )}
                      </motion.button>
                      {recording && (
                        <p className={`text-xs animate-pulse ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                          Recording… click stop when done
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className={`p-3 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-2 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                        Preview your recording ({fmtSecs(recordSecs)})
                      </p>
                      <audio controls className="w-full h-8 mb-3">
                        <source src={recordedUrl} type="audio/wav" />
                      </audio>
                      <div className="flex gap-2">
                        <button
                          onClick={saveRecording}
                          disabled={saving}
                          className={`flex-1 py-1.5 rounded-xl text-sm font-medium border transition disabled:opacity-50 ${
                            isDark
                              ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border-sky-500/30'
                              : 'bg-slate-900 hover:bg-slate-700 text-white border-transparent'
                          }`}
                        >
                          {saving ? 'Saving…' : 'Use This Greeting'}
                        </button>
                        <button
                          onClick={discardRecording}
                          disabled={saving}
                          className={`px-4 py-1.5 rounded-xl text-sm transition disabled:opacity-50 ${
                            isDark ? 'text-white/40 hover:text-white/60' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          Re-record
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Upload tab ── */}
              {activeTab === 'upload' && (
                <div>
                  <p className={`text-xs mb-4 ${textMuted}`}>
                    Upload an MP3 or WAV file (max 10 MB). This will replace your current greeting.
                  </p>

                  {!uploadFile ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]); }}
                      className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center gap-2 cursor-pointer transition ${
                        isDark
                          ? 'border-white/15 hover:border-white/30 text-white/40 hover:text-white/60'
                          : 'border-slate-300 hover:border-slate-400 text-slate-400 hover:text-slate-500'
                      }`}
                    >
                      <UploadIcon className="h-8 w-8" />
                      <p className="text-sm font-medium">Drop file here or click to browse</p>
                      <p className="text-xs">MP3 or WAV · max 10 MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".mp3,.wav,audio/mpeg,audio/mp3,audio/wav"
                        className="hidden"
                        onChange={e => handleFileSelect(e.target.files?.[0])}
                      />
                    </div>
                  ) : (
                    <div className={`p-3 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-1 truncate ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                        {uploadFile.name}
                      </p>
                      <p className={`text-xs mb-2 ${textMuted}`}>
                        {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <audio controls className="w-full h-8 mb-3">
                        <source src={uploadPreview} />
                      </audio>
                      <div className="flex gap-2">
                        <button
                          onClick={saveUpload}
                          disabled={saving}
                          className={`flex-1 py-1.5 rounded-xl text-sm font-medium border transition disabled:opacity-50 ${
                            isDark
                              ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border-sky-500/30'
                              : 'bg-slate-900 hover:bg-slate-700 text-white border-transparent'
                          }`}
                        >
                          {saving ? 'Uploading…' : 'Use This Greeting'}
                        </button>
                        <button
                          onClick={() => {
                            setUploadFile(null);
                            if (uploadPreview) { URL.revokeObjectURL(uploadPreview); setUploadPreview(''); }
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          disabled={saving}
                          className={`px-4 py-1.5 rounded-xl text-sm transition disabled:opacity-50 ${
                            isDark ? 'text-white/40 hover:text-white/60' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Feedback message */}
              <AnimatePresence>
                {msg.text && (
                  <motion.p
                    key="msg"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`text-xs mt-3 ${
                      msg.type === 'error'
                        ? (isDark ? 'text-red-400' : 'text-red-600')
                        : (isDark ? 'text-green-400' : 'text-green-600')
                    }`}
                  >
                    {msg.text}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main Voicemail component ──────────────────────────────────────────────────
export default function Voicemail({ theme = 'dark', onUnreadCount }) {
  const [voicemails, setVoicemails] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [playingId, setPlayingId]   = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const isDark = theme === 'dark';

  const text        = isDark ? 'text-white'                                   : 'text-slate-900';
  const textMuted   = isDark ? 'text-white/50'                                : 'text-slate-500';
  const textFaint   = isDark ? 'text-white/30'                                : 'text-slate-400';
  const surface     = isDark ? 'bg-white/5'                                   : 'bg-slate-50';
  const surfaceNew  = isDark ? 'bg-sky-500/10 border-sky-500/25'              : 'bg-blue-50 border-blue-200';
  const surfaceRead = isDark ? 'bg-white/[0.03] border-white/8'               : 'bg-slate-50 border-slate-200';
  const btnPrimary  = isDark
    ? 'bg-white/10 hover:bg-white/[0.15] text-white border border-white/15'
    : 'bg-slate-900 hover:bg-slate-700 text-white border-transparent';
  const btnGreen    = isDark
    ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/25'
    : 'bg-green-600 hover:bg-green-700 text-white border-transparent';
  const btnGray     = isDark
    ? 'bg-white/8 hover:bg-white/12 text-white/60 border border-white/10'
    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-transparent';

  useEffect(() => { fetchVoicemails(); }, []);

  const fetchVoicemails = async () => {
    try {
      setLoading(true);
      const response = await api.get('/voicemails', { params: { limit: 50 } });
      const data = response.data.data || [];
      setVoicemails(data);
      onUnreadCount?.(data.filter(v => !v.listened).length);
      setError('');
    } catch (err) {
      setError('Failed to load voicemails: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/voicemails/${id}`);
      setVoicemails(prev => prev.filter(v => v.id !== id));
      if (playingId === id) setPlayingId(null);
    } catch {
      setError('Failed to delete voicemail');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleMarkListened = async (id) => {
    try {
      await api.patch(`/voicemails/${id}/listen`);
      fetchVoicemails();
    } catch (err) {
      setError('Failed to mark as listened: ' + err.message);
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const unreadCount = voicemails.filter(v => !v.listened).length;

  return (
    <div className={text}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold tracking-tight">Voicemail</h2>
          {unreadCount > 0 && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
              isDark ? 'bg-sky-500/20 text-sky-400' : 'bg-blue-100 text-blue-700'
            }`}>
              {unreadCount} new
            </span>
          )}
        </div>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={fetchVoicemails}
          disabled={loading}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition disabled:opacity-40 border ${btnPrimary}`}
        >
          <RefreshIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : 'Refresh'}
        </motion.button>
      </div>

      {/* Greeting panel */}
      <GreetingPanel theme={theme} />

      {/* Error */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`border px-4 py-2.5 rounded-xl text-sm mb-4 ${
            isDark ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'bg-red-50 border-red-300 text-red-700'
          }`}
        >
          {error}
        </motion.div>
      )}

      {/* Loading skeleton */}
      {loading && voicemails.length === 0 && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`h-20 rounded-2xl animate-pulse ${surface}`} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && voicemails.length === 0 && !error && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-20 gap-4"
        >
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${surface}`}>
            <VoicemailIcon className={`h-7 w-7 ${textMuted}`} />
          </div>
          <div className="text-center">
            <p className={`font-medium ${text}`}>No voicemails</p>
            <p className={`text-sm mt-1 ${textMuted}`}>Missed calls will route to voicemail automatically</p>
          </div>
        </motion.div>
      )}

      {/* Voicemail list */}
      {voicemails.length > 0 && (
        <div className="space-y-3">
          {voicemails.map((vm, i) => (
            <motion.div
              key={vm.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.3) }}
              className={`border rounded-2xl p-4 transition ${vm.listened ? surfaceRead : surfaceNew}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {!vm.listened && (
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-sky-400' : 'bg-blue-500'}`} />
                    )}
                    <span className={`text-sm font-semibold ${text}`}>
                      {vm.fromNumber || 'Unknown caller'}
                    </span>
                    {!vm.listened && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        isDark ? 'bg-sky-500/20 text-sky-400' : 'bg-blue-100 text-blue-700'
                      }`}>New</span>
                    )}
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${textFaint}`}>
                    <span>{formatDate(vm.createdAt)}</span>
                    <span>·</span>
                    <span>{formatDuration(vm.durationSeconds)}</span>
                  </div>
                  {vm.transcription && (
                    <div className={`mt-3 p-3 rounded-xl border text-sm ${
                      isDark ? 'bg-white/5 border-white/10 text-white/70' : 'bg-white border-slate-200 text-slate-600'
                    }`}>
                      <span className={`text-xs font-semibold uppercase tracking-wide block mb-1 ${textFaint}`}>Transcription</span>
                      {vm.transcription}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setPlayingId(playingId === vm.id ? null : vm.id)}
                    className={`w-9 h-9 flex items-center justify-center rounded-xl border transition ${btnPrimary}`}
                    title={playingId === vm.id ? 'Pause' : 'Play'}
                  >
                    {playingId === vm.id ? <PauseIcon className="h-3.5 w-3.5" /> : <PlayIcon className="h-3.5 w-3.5" />}
                  </motion.button>

                  {!vm.listened && (
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleMarkListened(vm.id)}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border transition ${btnGreen}`}
                      title="Mark as listened"
                    >
                      <CheckIcon className="h-3.5 w-3.5" />
                    </motion.button>
                  )}

                  {vm.audioUrl && (
                    <motion.a
                      whileTap={{ scale: 0.95 }}
                      href={vm.audioUrl}
                      download
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border transition ${btnGray}`}
                      title="Download"
                    >
                      <DownloadIcon className="h-3.5 w-3.5" />
                    </motion.a>
                  )}

                  {confirmDeleteId === vm.id ? (
                    <div className="flex items-center gap-1.5">
                      <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleDelete(vm.id)}
                        disabled={deletingId === vm.id}
                        className={`px-2.5 h-9 flex items-center gap-1 rounded-xl border text-xs font-semibold transition disabled:opacity-50 ${
                          isDark
                            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30'
                            : 'bg-red-600 hover:bg-red-700 text-white border-transparent'
                        }`}
                      >
                        {deletingId === vm.id ? '…' : 'Delete'}
                      </motion.button>
                      <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setConfirmDeleteId(null)}
                        className={`w-9 h-9 flex items-center justify-center rounded-xl border text-xs transition ${btnGray}`}
                      >✕</motion.button>
                    </div>
                  ) : (
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setConfirmDeleteId(vm.id)}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border transition ${
                        isDark
                          ? 'bg-white/8 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 text-white/40 border-white/10'
                          : 'bg-slate-100 hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-slate-400 border-transparent'
                      }`}
                      title="Delete voicemail"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </motion.button>
                  )}
                </div>
              </div>

              <AnimatePresence>
                {playingId === vm.id && vm.audioUrl && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className={`mt-3 pt-3 border-t overflow-hidden ${isDark ? 'border-white/10' : 'border-slate-200'}`}
                  >
                    <audio controls autoPlay className="w-full h-8">
                      <source src={vm.audioUrl} type="audio/mp3" />
                    </audio>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
